import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  InputGroup,
  useToast,
  Text,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { ModalFormData, modalFormSchema } from './schemas';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePostAnthemMutation } from '@/services/queries/anthem';
import { TextareaField } from '@/components/TextareaField';
import { AudioPreview } from '@/components/AudioPreview';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface AnthemCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

export const AnthemCreateModal = ({ isOpen, onClose, onDone }: AnthemCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postItemMutation = usePostAnthemMutation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
    control,
    setValue,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: '',
      file: '',
      lyrics: '',
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      const payload = {
        ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
        file: data?.file,
        lyrics: data?.lyrics ?? '',
      };

      postItemMutation.mutate(payload, {
        onSuccess: () => {
          onDone?.();
          reset();
          toast({
            status: 'success',
            title: t('pages.application_history_anthem.anthem_create_modal.anthem_create_success'),
          });
        },
        onError: (resp: any) => {
          toast({
            status: 'error',
            title: (resp && resp.message) || t('pages.application_history_anthem.anthem_create_modal.anthem_create_error'),
          });
        },
      });
    },
    [onDone, postItemMutation, reset, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_history_anthem.anthem_create_modal.anthem_create_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_history_anthem.anthem_create_modal.anthem_create_team')}
              placeholder={t('pages.application_history_anthem.anthem_create_modal.anthem_create_team')}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />
            <Text fontWeight='medium'>{t('pages.application_history_anthem.anthem_create_modal.anthem_label_lyrics')}</Text>
            <TextareaField
              rows={6}
              maxLength={2000}
              helperText={t('pages.application_history_anthem.anthem_create_modal.anthem_helper_text')}
              error={errors?.lyrics?.message}
              {...register('lyrics')}
            />

            <InputGroup display='flex' flexDirection='column' width='100%'>
              <AudioPreview
                fileUrl={''}
                onFileChange={file => {
                  setValue('file', file);
                }}
                onDelete={async () => {
                  console.log('');
                }}
                error={errors?.file?.message?.toString()}
              />
            </InputGroup>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w='100%' justifyContent={'space-between'} alignItems='center' gap={2} paddingLeft={6}>
            <Flex w={'100%'} justifyContent='end' gap={5}>
              <Button variant='outlined' color='gray.600' onClick={onClose}>
                {t('pages.application_history_anthem.anthem_create_modal.anthem_create_cancel')}
              </Button>
              <Button onClick={handleConfirm} bg='green.500' paddingX={10}>
                {t('pages.application_history_anthem.anthem_create_modal.anthem_create_create')}
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={postItemMutation.isLoading} />
    </Modal>
  );
};
